import type { GetFormCustomisationResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetAllFormsCustomisationQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetAllFormsCustomisationDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook, REFERENCE_DATA_STALE_TIME_MS } from 'src/utils';

import { mapFormCustomisationTrpcToGraphQL } from './mapFormCustomisationTrpcToGraphQL';

/**
 * Flag-gated ALL-forms customisation read (no ParentType filter) — backs the
 * custom-data-source pages/widgets that need every form's field configuration.
 *
 * tRPC path: `getFormCustomisation` with an empty `parentTypes` array, which
 * the data-layer treats as "no filter => all forms" (mirrors `getByParentTypes`).
 * GraphQL fallback: the unfiltered `GetAllFormsCustomisationDocument`, NOT
 * `getFormCustomisation` with `parentTypes: []` — the latter would compile to
 * `where: { ParentType: { _in: [] } }` and return zero rows on the Apollo path.
 *
 * Takes no query args; pass `shouldSkip` to gate it (the all-forms read is
 * heavy and the vitest setup promotes console.warn to fatal, so consumers that
 * don't yet need the data should skip).
 */
export const useGetAllFormsCustomisation = createQueryHook<
  Record<string, never>,
  GetFormCustomisationResponseRow[],
  GetAllFormsCustomisationQuery
>({
  trpcQueryOptions: (trpc) =>
    trpc.frontend.formConfiguration.getFormCustomisation.queryOptions({
      parentTypes: [],
    }),
  // Same nominal-mismatch cast as `useGetFormCustomisationByParentTypes`:
  // Drizzle's InferQueryModel widens `FormConfigurationParentType` to `string`
  // while the GraphQL codegen narrows it to `Parent_Type_Enum`. Structurally
  // identical (same literal strings), nominally distinct — route via `unknown`.
  mapTrpcDataToGraphQL: (data) => mapFormCustomisationTrpcToGraphQL(data),
  // Org reference data; writes invalidate it. See COI-882.
  trpcStaleTime: REFERENCE_DATA_STALE_TIME_MS,
  graphqlDocument: GetAllFormsCustomisationDocument,
  graphqlVariables: () => ({}),
});
